'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Dumbbell, TrendingUp, TrendingDown, Minus, UserPlus, Share2 } from 'lucide-react';
import ScoreBadge from '@/components/ui/ScoreBadge';
import ShareProgramModal from './ShareProgramModal';

interface RosterEntry {
  athlete: {
    id: string;
    name: string;
    sport: string;
    position: string;
    seasonPhase: string;
    avatarInitials: string;
    email?: string;
    inviteStatus?: string;
  };
  session: {
    id: string;
    date: string;
    eps: number;
    engineScore: number;
    ignitionScore: number;
    bounceScore: number;
  } | null;
  profile: any;
  flag: 'green' | 'yellow' | 'red';
  weakestCategory: string;
  trend: 'up' | 'neutral' | 'down';
}

type SortKey = 'eps' | 'engineScore' | 'ignitionScore' | 'bounceScore' | 'name';

const FLAG_STYLES = {
  green: { bg: 'bg-emerald-400/10', border: 'border-emerald-400/25', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  yellow: { bg: 'bg-amber-400/10', border: 'border-amber-400/25', text: 'text-amber-400', dot: 'bg-amber-400' },
  red: { bg: 'bg-red-400/10', border: 'border-red-400/25', text: 'text-red-400', dot: 'bg-red-400' },
};

const WEAK_COLOR: Record<string, string> = {
  Engine: '#EF4444',
  Ignition: '#F59E0B',
  Bounce: '#10B981',
  'N/A': '#71717a',
};

function TrendIcon({ trend }: { trend: string }) {
  if (trend === 'up') return <TrendingUp size={13} className="text-emerald-400" />;
  if (trend === 'down') return <TrendingDown size={13} className="text-red-400" />;
  return <Minus size={13} className="text-zinc-500" />;
}

export default function AthleteRosterTable({ roster, canAddAthlete, onAddAthlete, coachId }: {
  roster: RosterEntry[];
  canAddAthlete?: boolean;
  onAddAthlete?: () => void;
  coachId?: string;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('eps');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [flagFilter, setFlagFilter] = useState<'all' | 'red' | 'yellow' | 'green'>('all');
  const [search, setSearch] = useState('');
  const [shareModalAthlete, setShareModalAthlete] = useState<RosterEntry['athlete'] | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const filtered = roster
    .filter(r => {
      if (flagFilter !== 'all' && r.flag !== flagFilter) return false;
      if (search && !r.athlete.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let aVal: number | string = 0;
      let bVal: number | string = 0;
      if (sortKey === 'eps') { aVal = a.session?.eps ?? 0; bVal = b.session?.eps ?? 0; }
      else if (sortKey === 'engineScore') { aVal = a.session?.engineScore ?? 0; bVal = b.session?.engineScore ?? 0; }
      else if (sortKey === 'ignitionScore') { aVal = a.session?.ignitionScore ?? 0; bVal = b.session?.ignitionScore ?? 0; }
      else if (sortKey === 'bounceScore') { aVal = a.session?.bounceScore ?? 0; bVal = b.session?.bounceScore ?? 0; }
      else if (sortKey === 'name') { aVal = a.athlete.name; bVal = b.athlete.name; }
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal);
      return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ArrowUpDown size={12} className="text-zinc-600 ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp size={12} className="ml-1" style={{ color: '#E10600' }} />
      : <ArrowDown size={12} className="ml-1" style={{ color: '#E10600' }} />;
  }

  if (roster.length === 0) {
    return (
      <div className="card p-10 text-center">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(225,6,0,0.08)', border: '1px solid rgba(225,6,0,0.15)' }}
        >
          <UserPlus size={22} style={{ color: '#E10600' }} />
        </div>
        <p className="text-sm font-medium text-zinc-300 mb-1">No athletes added yet.</p>
        <p className="text-xs text-zinc-500 mb-5">Start building your roster by adding your first athlete.</p>
        {canAddAthlete && onAddAthlete && (
          <button
            onClick={onAddAthlete}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, #E10600, #8B0000)',
              color: '#ffffff',
              boxShadow: '0 0 12px rgba(225,6,0,0.25)',
            }}
          >
            <UserPlus size={13} />
            Add Athlete
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="section-header">Athlete Roster</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {canAddAthlete && onAddAthlete && (
            <button
              onClick={onAddAthlete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150"
              style={{
                background: 'rgba(225,6,0,0.1)',
                border: '1px solid rgba(225,6,0,0.3)',
                color: '#FF3B30',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(225,6,0,0.18)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(225,6,0,0.1)';
              }}
            >
              <UserPlus size={12} />
              Add Athlete
            </button>
          )}
          <input
            type="text"
            placeholder="Search athletes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field w-44 py-1.5"
          />
          {(['all', 'green', 'yellow', 'red'] as const).map(f => (
            <button
              key={`flag-filter-${f}`}
              onClick={() => setFlagFilter(f)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150"
              style={flagFilter === f ? {
                background: 'rgba(225,6,0,0.1)',
                border: '1px solid rgba(225,6,0,0.3)',
                color: '#E10600',
              } : {
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#71717a',
              }}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin rounded-xl border border-zinc-800">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-800/40">
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <button onClick={() => handleSort('name')} className="flex items-center hover:text-zinc-300 transition-colors">
                  Athlete <SortIcon col="name" />
                </button>
              </th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Sport / Position</th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <button onClick={() => handleSort('eps')} className="flex items-center hover:text-zinc-300 transition-colors">
                  EPS <SortIcon col="eps" />
                </button>
              </th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <button onClick={() => handleSort('engineScore')} className="flex items-center hover:text-zinc-300 transition-colors">
                  Engine <SortIcon col="engineScore" />
                </button>
              </th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <button onClick={() => handleSort('ignitionScore')} className="flex items-center hover:text-zinc-300 transition-colors">
                  Ignition <SortIcon col="ignitionScore" />
                </button>
              </th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <button onClick={() => handleSort('bounceScore')} className="flex items-center hover:text-zinc-300 transition-colors">
                  Bounce <SortIcon col="bounceScore" />
                </button>
              </th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Weakest</th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Trend</th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Flag</th>
              <th className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Last Tested</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={11} className="py-8 text-center text-sm text-zinc-500">
                  No athletes match your filter.
                </td>
              </tr>
            ) : filtered.map(row => {
              const flagStyle = FLAG_STYLES[row.flag];
              const lastTested = row.session
                ? new Date(row.session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'Never tested';

              return (
                <tr key={row.athlete.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors group">
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-zinc-400">{row.athlete.avatarInitials}</span>
                      </div>
                      <div>
                        <p className="font-medium text-zinc-200 whitespace-nowrap">{row.athlete.name}</p>
                        <p className="text-xs text-zinc-600">{row.athlete.seasonPhase}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-zinc-300 whitespace-nowrap text-xs">{row.athlete.sport || '—'}</p>
                    <p className="text-zinc-600 text-xs">{row.athlete.position || '—'}</p>
                  </td>
                  <td className="py-3 px-3">
                    {row.session ? <ScoreBadge score={row.session.eps} /> : <span className="text-zinc-600 text-xs">Not tested</span>}
                  </td>
                  <td className="py-3 px-3">
                    {row.session ? <ScoreBadge score={row.session.engineScore} showLabel={false} /> : '—'}
                  </td>
                  <td className="py-3 px-3">
                    {row.session ? <ScoreBadge score={row.session.ignitionScore} showLabel={false} /> : '—'}
                  </td>
                  <td className="py-3 px-3">
                    {row.session ? <ScoreBadge score={row.session.bounceScore} showLabel={false} /> : '—'}
                  </td>
                  <td className="py-3 px-3">
                    <span className="text-xs font-semibold" style={{ color: WEAK_COLOR[row.weakestCategory] }}>
                      {row.weakestCategory}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <TrendIcon trend={row.trend} />
                  </td>
                  <td className="py-3 px-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${flagStyle.bg} ${flagStyle.border} ${flagStyle.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${flagStyle.dot}`} />
                      {row.flag.charAt(0).toUpperCase() + row.flag.slice(1)}
                    </span>
                  </td>
                  <td className="py-3 px-3 font-mono text-xs text-zinc-500 whitespace-nowrap">{lastTested}</td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link
                        href={`/explosive-profile-report?athleteId=${row.athlete.id}`}
                        title="View Profile"
                        className="p-1.5 rounded-lg hover:bg-zinc-700 text-zinc-500 hover:text-zinc-200 transition-all"
                      >
                        <ExternalLink size={13} />
                      </Link>
                      <Link
                        href={`/training-program-builder?athleteId=${row.athlete.id}`}
                        title="Generate Program"
                        className="p-1.5 rounded-lg hover:bg-zinc-700 text-zinc-500 hover:text-zinc-200 transition-all"
                      >
                        <Dumbbell size={13} />
                      </Link>
                      {coachId && (
                        <button
                          onClick={() => setShareModalAthlete(row.athlete)}
                          title="Share Program with Athlete"
                          className="p-1.5 rounded-lg hover:bg-zinc-700 text-zinc-500 hover:text-zinc-200 transition-all"
                        >
                          <Share2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
        <span>Showing {filtered.length} of {roster.length} athletes</span>
        <div className="flex items-center gap-3">
          {Object.entries(FLAG_STYLES).map(([flag, style]) => (
            <span key={`legend-flag-${flag}`} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${style.dot}`} />
              <span className="capitalize">{flag}</span>
            </span>
          ))}
        </div>
      </div>

      {shareModalAthlete && coachId && (
        <ShareProgramModal
          athleteId={shareModalAthlete.id}
          athleteName={shareModalAthlete.name}
          athleteEmail={shareModalAthlete.email ?? ''}
          coachId={coachId}
          onClose={() => setShareModalAthlete(null)}
        />
      )}
    </div>
  );
}